import { createSelector } from "reselect";
import { createAssign } from "robodux";
import { call, put, select } from "saga-query";

import { ensureArray } from "@app/service/helpers";
import { thunks } from "@app/state/apis/api";
import * as appConst from "@app/state/constants";
import { readUserRoles } from "@app/state/units/admins";
import { selectAllUsersList } from "@app/state/units/users";

import { selectAllGroupsListArray } from "../groups";

import type { RootState } from "@app/state/rootState";
import type { TUserTreeListItem, IAdminGroupUser } from "@app/types";
import type { IGroupMember } from "@app/types";
// const commandItems : TUserTreeListItem[] = [];

const REPO_USER_TREELIST = "ui/users/treelist";
type TRepoUserTreeList = {
  currentItem: TUserTreeListItem | undefined;
};
export const repoUserTreeList = createAssign<TRepoUserTreeList>({
  name: REPO_USER_TREELIST,
  initialState: {
    currentItem: undefined,
  },
});

const selectUserTreeStore = (state: RootState) => state[REPO_USER_TREELIST];
export const selectCurrentUserTreeList = createSelector(
  selectUserTreeStore,
  (state) => state?.currentItem
);
export const selectCurrentEmail = createSelector(
  selectCurrentUserTreeList,
  (item) => item?.id
);

export const selectUserTreeList = createSelector(
  selectAllUsersList,
  (users) => {
    const users2 = ensureArray(Object.values(users));
    if (!users2?.length) return [];
    // UNIQUE USERS BY EMAIL:
    const userItems = users2
      .reduce((acc: IAdminGroupUser[], user: IAdminGroupUser) => {
        const found = acc.find((u) => u.OUSER_EMAIL === user.OUSER_EMAIL);
        if (!found) {
          if (user.OUSER_EMAIL) {
            acc.push(user);
          }
        }
        return acc;
      }, [])
      .map((user: IAdminGroupUser) => {
        return {
          id: user.OUSER_EMAIL,
          parentId: null,
          ptlTitle: user.OUSER_EMAIL,
          ptlDetail: user.OUSER_NAME,
          ptlClassName: "user",
          ptlGroup: "user",
          ptlCfgPage: appConst.USERS_PAGES.USER,
          ptlSortPosition: 1,
          ptlMeta: JSON.stringify(user),
        } as TUserTreeListItem;
      });
    return userItems;
  }
);

export const setCurrentUserTreeListItem = thunks.create<TUserTreeListItem>(
  "/setCurrentUserTreeListItem",
  function* (ctx, next) {
    const crtstate = yield* select(selectUserTreeStore);
    yield* put(
      repoUserTreeList.actions.set({ ...crtstate, currentItem: ctx.payload })
    );
    yield* call(
      readUserRoles.run,
      readUserRoles({ id: ctx.payload.id as string })
    );
  }
);

export const selectAllUserGroups = createSelector(
  selectAllUsersList,
  selectCurrentEmail,
  (t, email) => {
    const data = Object.values(t).filter((u) => u && u.OUSER_EMAIL === email);
    return data;
  }
);
export type TGroupMemberPicker = {
  GROUPID: number;
  PERSEED: string;
  INSIDERS_READY: boolean;
  disabled: boolean;
};

export const selectAllGroupIdPerseed = createSelector(
  selectAllGroupsListArray,
  selectAllUserGroups,
  (t, crtGroups): TGroupMemberPicker[] => {
    const crtGroupsIds = crtGroups.map((g) => g?.GROUPID);
    return Object.values(t)
      .map((g: IGroupMember) => ({
        GROUPID: g.GROUPID,
        PERSEED: g.PERSEED,
        INSIDERS_READY: g.INSIDERS_READY,
        disabled: crtGroupsIds.includes(g.GROUPID),
      }))
      .concat({
        GROUPID: -1,
        PERSEED: "",
        INSIDERS_READY: false,
        disabled: true,
      } as TGroupMemberPicker);
  }
);

interface TheCCIdxByCode {
  [key: string]: TGroupMemberPicker;
}
interface TheCCIdxById {
  [key: number]: TGroupMemberPicker;
}
export const selectAllGroupsIdxPerseed = createSelector(
  selectAllGroupIdPerseed,
  (p) =>
    p.reduce<TheCCIdxByCode>((acc, cur) => {
      const k = cur.PERSEED;
      if (!k) return acc;
      acc[k] = cur;
      return acc;
    }, {})
);

export const selectAllGroupsIdxById = createSelector(
  selectAllGroupIdPerseed,
  (p) =>
    p.reduce<TheCCIdxById>((acc, cur) => {
      const k = cur.GROUPID;
      if (!k) return acc;
      acc[k] = cur;
      return acc;
    }, {})
);
